package com.solevo.dist.network.firebase.api.notifications

import com.solevo.dist.application.models.Notification
import com.solevo.dist.application.models.NotificationStatus
import com.solevo.dist.application.models.NotificationType
import com.solevo.dist.application.models.User.Companion.legalId
import com.solevo.dist.application.models.User.Companion.userContactId
import com.solevo.dist.network.api.notifications.NotificationRemoteSource
import com.solevo.dist.network.firebase.api.notifications.models.FirebaseNotification
import com.solevo.dist.network.firebase.api.notifications.models.toNotification
import com.solevo.dist.network.implementation.firebase.FirebaseFields
import com.solevo.dist.network.implementation.firebase.FirestoreCollections
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull

class NotificationFirebaseRemoteSource(
    private val db: dev.gitlive.firebase.firestore.FirebaseFirestore = Firebase.firestore
): NotificationRemoteSource {

    override fun getNotification(id: String): Flow<Notification?> {
        return db.collection(FirestoreCollections.NOTIFICATIONS_COLLECTION)
            .where {
                all(
                    FirebaseFields.NOTIFICATION_ID.equalTo(id),
                    FirebaseFields.LEGAL_ENTITY_ID.equalTo(legalId)
                )
            }
            .snapshots()
            .map { querySnapshot ->
                if (querySnapshot.documents.isEmpty()) {
                    null
                } else {
                    querySnapshot.documents.map { 
                        it.data<FirebaseNotification>() 
                    }.first().toNotification()
                }
            }
    }

    override fun getNotificationsLive(limit: Int, type: NotificationType?): Flow<List<Notification>> {
        val types = if (type != null) {
            listOf(type.name)
        } else {
            NotificationType.entries.map { it.name }
        }

        return db.collection(FirestoreCollections.NOTIFICATIONS_COLLECTION)
            .orderBy(FirebaseFields.TIMESTAMP, Direction.DESCENDING)
            .where {
                all(
                    FirebaseFields.CONTACT_ID.equalTo(userContactId),
                    FirebaseFields.STATUS.inArray(
                        listOf(NotificationStatus.New.name, NotificationStatus.Read.name)
                    ),
                    FirebaseFields.CLASSIFICATION.inArray(types),
                    FirebaseFields.LEGAL_ENTITY_ID.equalTo(legalId)
                )
            }
            .limit(limit.toLong())
            .snapshots()
            .map { querySnapshot ->
                querySnapshot.documents.map { 
                    it.data<FirebaseNotification>().toNotification() 
                }
            }
    }

    override fun checkUnreadNotifications(): Flow<Boolean> {
        return db.collection(FirestoreCollections.NOTIFICATIONS_COLLECTION)
            .where {
                all(
                    FirebaseFields.CONTACT_ID.equalTo(userContactId),
                    FirebaseFields.LEGAL_ENTITY_ID.equalTo(legalId),
                    FirebaseFields.STATUS.equalTo(NotificationStatus.New.name)
                )
            }
            .limit(1)
            .snapshots()
            .map { querySnapshot ->
                querySnapshot.documents.isNotEmpty()
            }
    }

    override suspend fun updateNotification(id: String, data: Map<String, Any>): Result<Unit> {
        return runCatching {
            db.collection(FirestoreCollections.NOTIFICATIONS_COLLECTION)
                .where {
                    all(
                        FirebaseFields.NOTIFICATION_ID.equalTo(id),
                        FirebaseFields.LEGAL_ENTITY_ID.equalTo(legalId)
                    )
                }
                .snapshots()
                .first().documents.first().reference
                .update(data)
        }
    }
}

